import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { MAX_VENDOR_PORTAL_FILES_PER_ARCHIVE } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';

/**
 * Rows per page the user can pick. None is past the zip cap, so a whole page (and so any
 * selection on it) always downloads as one zip; more files are downloaded a page at a time.
 */
export const VENDOR_PORTAL_FILES_PAGE_SIZES = [
  10,
  25,
  MAX_VENDOR_PORTAL_FILES_PER_ARCHIVE,
] as const;
export const DEFAULT_VENDOR_PORTAL_FILES_PAGE_SIZE =
  MAX_VENDOR_PORTAL_FILES_PER_ARCHIVE;

/** The filters the list and the zip download share. */
export interface VendorPortalFileFilters {
  vendor?: string;
  account?: string;
  /** A file-name extension without the dot (`xlsx`). */
  fileType?: string;
  downloadedFrom?: Date;
  downloadedBefore?: Date;
  workflowRunId?: string;
}

export interface VendorPortalFilesOptions {
  vendor?: string;
  account?: string;
  /** A file-name extension without the dot (`xlsx`). */
  fileType?: string;
  /** Only files downloaded at or after this instant. */
  downloadedFrom?: Date;
  /** Only files downloaded before this instant. */
  downloadedBefore?: Date;
  workflowRunId?: string;
  limit: number;
  offset: number;
}

/** One page of the files workflows downloaded from vendor portals. */
export function useVendorPortalFiles({
  vendor,
  account,
  fileType,
  downloadedFrom,
  downloadedBefore,
  workflowRunId,
  limit,
  offset,
}: VendorPortalFilesOptions) {
  return useQuery({
    ...orpc.pricing.vendorPortalFiles.list.queryOptions({
      input: {
        ...(vendor ? { vendor } : {}),
        ...(account ? { account } : {}),
        ...(fileType ? { fileType } : {}),
        ...(downloadedFrom ? { downloadedFrom } : {}),
        ...(downloadedBefore ? { downloadedBefore } : {}),
        ...(workflowRunId ? { workflowRunId } : {}),
        limit,
        offset,
      },
    }),
    // Keep the current page on screen while the next one loads.
    placeholderData: keepPreviousData,
  });
}

/** Permanently delete files, then refresh the list (and its vendor counts). */
export function useDeleteVendorPortalFiles() {
  const queryClient = useQueryClient();
  return useMutation({
    ...orpc.pricing.vendorPortalFiles.delete.mutationOptions(),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: orpc.pricing.vendorPortalFiles.list.key(),
      }),
  });
}
